import { useNavigate } from 'react-router';
import { useLogout, useMe } from '../lib/auth';

function initials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

/**
 * The signed-in identity: avatar (or initials in a lime-outlined square), username, an ADMIN chip
 * when applicable, and a plain Log out button. No popover, no settings link — WP2 keeps this
 * minimal on purpose. Row on the mobile top strip, column in the desktop rail's narrow foot.
 */
export function UserMenu() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();

  if (!me) {
    return null;
  }

  return (
    <div
      className="flex min-w-0 items-center gap-2 md:w-full md:flex-col md:items-center md:gap-1.5"
      data-testid="user-menu"
      title={me.username}
    >
      {me.avatarUrl ? (
        <img
          src={me.avatarUrl}
          alt=""
          data-testid="user-avatar"
          width={28}
          height={28}
          className="size-7 shrink-0 rounded-sm border border-border-strong"
        />
      ) : (
        <span
          aria-hidden="true"
          data-testid="user-avatar-initials"
          className="flex size-7 shrink-0 items-center justify-center rounded-sm border border-accent-border font-display text-xs font-bold text-accent"
        >
          {initials(me.username)}
        </span>
      )}

      {/* The 72px rail cannot fit a Discord username; there it lives in the title tooltip and for
          screen readers only. The mobile strip has room, so it shows. */}
      <span className="min-w-0 truncate text-sm font-semibold text-text md:sr-only">
        {me.username}
      </span>

      {me.role === 'ADMIN' && (
        <span className="shrink-0 rounded-sm border border-accent-border bg-accent-dim px-1.5 py-0.5 font-mono text-[0.625rem] font-semibold uppercase tracking-wider text-accent">
          Admin
        </span>
      )}

      <button
        type="button"
        disabled={logout.isPending}
        onClick={() => {
          logout.mutate(undefined, {
            onSettled: () => navigate('/login', { replace: true }),
          });
        }}
        className="min-h-tap shrink-0 rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted transition-colors hover:border-accent-border hover:text-text disabled:opacity-50 md:w-full md:px-0 md:text-xs"
      >
        Log out
      </button>
    </div>
  );
}
